"use client";

import { useState, useEffect, useCallback, useRef } from "react";
import { RefreshCw, TrendingUp, Clock, Target, Zap, ExternalLink } from "lucide-react";
import { useVisibilityRef } from "@/hooks/useVisibilityRef";
import Image from "next/image";
import Link from "next/link";

// X (Twitter) logo icon component
const XIcon = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 24 24" className={className} fill="currentColor">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);

interface XTrackerContextSidebarProps {
  handle?: string;
  title?: string;    // Market title for tracking matching (e.g. "Elon Musk # tweets February 20 - February 27, 2026?")
  startDate: string; // ISO date: YYYY-MM-DD
  endDate: string;   // ISO date: YYYY-MM-DD
  targetCount?: number; // Optional target for the market
}

interface DailyMetric {
  date: string;
  count: number;
  cumulative: number;
  tweets?: number;
  replies?: number;
}

interface TrackerData {
  user: {
    handle: string;
    name: string;
  };
  metrics: {
    daily: DailyMetric[];
    totalInPeriod: number;
  };
  source: "xtracker";
  fetchedAt: string;
  tracking?: {
    endDate: string;
  };
}

export default function XTrackerContextSidebar({
  handle = "elonmusk",
  title,
  startDate,
  endDate,
  targetCount,
}: XTrackerContextSidebarProps) {
  const [data, setData] = useState<TrackerData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [countdown, setCountdown] = useState(30); // 30 seconds refresh
  const [, setTick] = useState(0); // Force re-render for time remaining
  const visRef = useVisibilityRef();

  // Pulse effect state
  const [isPulsing, setIsPulsing] = useState(false);
  const [newTweetsCount, setNewTweetsCount] = useState(0);
  const prevTotalRef = useRef<number | null>(null);
  const pulseTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const fetchData = useCallback(async () => {
    try {
      const params = new URLSearchParams({ handle, startDate, endDate });
      if (title) params.set("title", title);

      const res = await fetch(`/api/xtracker?${params.toString()}`);

      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.error || "Failed to fetch data");
      }

      const result = await res.json();

      const normalizedData: TrackerData = {
        user: result.user,
        metrics: {
          daily: result.metrics.daily,
          totalInPeriod: result.metrics.totalInPeriod,
        },
        source: "xtracker",
        fetchedAt: result.fetchedAt,
        tracking: result.tracking ? { endDate: result.tracking.endDate } : undefined,
      };

      // Check for new tweets (pulse effect)
      const currentTotal = normalizedData.metrics.totalInPeriod || 0;
      if (prevTotalRef.current !== null && currentTotal > prevTotalRef.current) {
        const diff = currentTotal - prevTotalRef.current;
        setNewTweetsCount(diff);
        setIsPulsing(true);

        // Clear previous timeout
        if (pulseTimeoutRef.current) {
          clearTimeout(pulseTimeoutRef.current);
        }

        // Stop pulsing after 4 seconds
        pulseTimeoutRef.current = setTimeout(() => {
          setIsPulsing(false);
          setNewTweetsCount(0);
        }, 4000);
      }
      prevTotalRef.current = currentTotal;

      setData(normalizedData);
      setError(null);
      setLastUpdated(new Date());
      setCountdown(30); // Refresh every 30 seconds
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [handle, title, startDate, endDate]);

  // Cleanup pulse timeout on unmount
  useEffect(() => {
    return () => {
      if (pulseTimeoutRef.current) {
        clearTimeout(pulseTimeoutRef.current);
      }
    };
  }, []);

  // Initial fetch and polling every 60 seconds
  useEffect(() => {
    fetchData();

    const pollInterval = setInterval(() => { if (visRef.current) fetchData(); }, 60000);

    return () => clearInterval(pollInterval);
  }, [fetchData]);

  // Countdown timer + time remaining update
  useEffect(() => {
    const countdownInterval = setInterval(() => {
      setCountdown((prev) => (prev <= 1 ? 60 : prev - 1));
      setTick((prev) => prev + 1); // Force re-render for time remaining
    }, 1000);

    return () => clearInterval(countdownInterval);
  }, []);

  // Calculate time remaining until end date
  const getTimeRemaining = () => {
    const now = new Date();
    // Use the tracking endDate (which has exact time) if available, otherwise fallback
    const endDateTime = data?.tracking?.endDate
      ? new Date(data.tracking.endDate)
      : new Date(endDate + "T22:00:00Z"); // Fallback to 5 PM ET (22:00 UTC)

    const diff = endDateTime.getTime() - now.getTime();

    if (diff <= 0) return "Ended";

    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((diff % (1000 * 60)) / 1000);

    if (days > 0) return `${days}d ${hours}h ${minutes}m`;
    if (hours > 0) return `${hours}h ${minutes}m ${seconds}s`;
    return `${minutes}m ${seconds}s`;
  };

  // Format date for display
  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  };

  if (loading && !data) {
    return (
      <div className="bg-white dark:bg-[#18181D] rounded-[5px] border border-gray-200 dark:border-[#1A1A1A] p-4">
        <div className="flex items-center gap-2 mb-4">
          <XIcon className="w-5 h-5 text-gray-900 dark:text-white animate-pulse" />
          <span className="text-gray-900 dark:text-white font-satoshi font-bold">Loading XTracker...</span>
        </div>
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="animate-pulse">
              <div className="h-10 bg-gray-200 dark:bg-[#0f0f12] rounded-[5px]" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (error && !data) {
    return (
      <div className="bg-white dark:bg-[#18181D] rounded-[5px] border border-red-500/30 p-4">
        <div className="flex items-center gap-2 mb-2">
          <XIcon className="w-5 h-5 text-red-500 dark:text-red-400" />
          <span className="text-gray-900 dark:text-white font-satoshi font-bold">XTracker Error</span>
        </div>
        <p className="text-red-500 dark:text-red-400 text-sm">{error}</p>
        <button
          onClick={fetchData}
          className="mt-3 text-xs text-gray-700 dark:text-white hover:underline flex items-center gap-1"
        >
          <RefreshCw className="w-3 h-3" /> Retry
        </button>
      </div>
    );
  }

  const totalPosts = data?.metrics?.totalInPeriod || 0;
  const dailyMetrics = data?.metrics?.daily || [];
  const timeRemaining = getTimeRemaining();
  const isEnded = timeRemaining === "Ended";

  // Show all days in the period
  const chartData = dailyMetrics;
  const maxCount = Math.max(...chartData.map((d) => d.count), 1);

  // Calculate progress percentage if we have a target
  const progressPercent = targetCount ? Math.min((totalPosts / targetCount) * 100, 100) : 0;

  return (
    <div className={`relative bg-white dark:bg-[#18181D] rounded-[5px] border overflow-hidden transition-colors ${
      isPulsing
        ? "border-[#7C3AED] dark:border-[#CCFF00]"
        : "border-gray-200 dark:border-[#1A1A1A]"
    }`}>
      {/* Pulse overlay */}
      {isPulsing && (
        <div className="absolute inset-0 bg-[#7C3AED]/5 dark:bg-[#CCFF00]/5 animate-pulse pointer-events-none z-10" />
      )}

      {/* New tweet notification */}
      {isPulsing && newTweetsCount > 0 && (
        <div className="absolute top-12 left-1/2 -translate-x-1/2 z-20">
          <div className="flex items-center gap-1.5 bg-[#7C3AED] dark:bg-[#CCFF00] text-white dark:text-black px-4 py-2 rounded-[5px] text-sm font-bold animate-bounce shadow-lg">
            <Zap className="w-4 h-4" />
            +{newTweetsCount} NEW TWEET{newTweetsCount > 1 ? "S" : ""}!
          </div>
        </div>
      )}

      {/* Cover Image */}
      <div className="relative h-24 overflow-hidden">
        <Image
          src={`https://unavatar.io/twitter/${data?.user?.handle || handle}`}
          alt="Cover"
          fill
          className="object-cover object-center blur-sm scale-110"
          unoptimized
        />
        <div className="absolute inset-0 bg-gradient-to-t from-white dark:from-[#18181D] via-transparent to-transparent" />

        {/* Refresh indicator on cover */}
        <div className="absolute top-2 right-2 flex items-center gap-1 text-gray-600 dark:text-white/60 text-[10px] bg-white/70 dark:bg-black/40 px-2 py-1 rounded-[5px] backdrop-blur-sm">
          <RefreshCw className={`w-3 h-3 ${loading ? "animate-spin" : ""}`} />
          <span>{countdown}s</span>
        </div>
      </div>

      {/* Profile section - overlapping cover */}
      <div className="relative px-4 -mt-8">
        <div className="flex items-end gap-3 mb-3">
          {/* Avatar from Twitter */}
          <div className="relative">
            <div className="w-16 h-16 rounded-[5px] overflow-hidden ring-4 ring-white dark:ring-[#18181D] bg-white dark:bg-[#18181D]">
              <Image
                src={`https://unavatar.io/twitter/${data?.user?.handle || handle}`}
                alt={data?.user?.name || handle}
                width={64}
                height={64}
                className="object-cover"
                unoptimized
              />
            </div>
            {/* X badge - links to profile */}
            <a
              href={`https://x.com/${data?.user?.handle || handle}`}
              target="_blank"
              rel="noopener noreferrer"
              className="absolute -bottom-1 -right-1 w-5 h-5 bg-gray-900 dark:bg-black rounded-[5px] flex items-center justify-center ring-2 ring-white dark:ring-[#18181D] hover:bg-gray-700 dark:hover:bg-white/20 transition-colors"
            >
              <XIcon className="w-3 h-3 text-white" />
            </a>
          </div>

          {/* Name and handle */}
          <div className="flex-1 pb-1">
            <div className="flex items-center gap-1.5">
              <h3 className="text-gray-900 dark:text-white font-satoshi font-bold text-base">
                {data?.user?.name || handle}
              </h3>
              {/* Verified badge */}
              <svg className="w-4 h-4 text-[#1D9BF0]" viewBox="0 0 22 22" fill="currentColor">
                <path d="M20.396 11c-.018-.646-.215-1.275-.57-1.816-.354-.54-.852-.972-1.438-1.246.223-.607.27-1.264.14-1.897-.131-.634-.437-1.218-.882-1.687-.47-.445-1.053-.75-1.687-.882-.633-.13-1.29-.083-1.897.14-.273-.587-.704-1.086-1.245-1.44S11.647 1.62 11 1.604c-.646.017-1.273.213-1.813.568s-.969.854-1.24 1.44c-.608-.223-1.267-.272-1.902-.14-.635.13-1.22.436-1.69.882-.445.47-.749 1.055-.878 1.688-.13.633-.08 1.29.144 1.896-.587.274-1.087.705-1.443 1.245-.356.54-.555 1.17-.574 1.817.02.647.218 1.276.574 1.817.356.54.856.972 1.443 1.245-.224.606-.274 1.263-.144 1.896.13.634.433 1.218.877 1.688.47.443 1.054.747 1.687.878.633.132 1.29.084 1.897-.136.274.586.705 1.084 1.246 1.439.54.354 1.17.551 1.816.569.647-.016 1.276-.213 1.817-.567s.972-.854 1.245-1.44c.604.239 1.266.296 1.903.164.636-.132 1.22-.447 1.68-.907.46-.46.776-1.044.908-1.681.132-.637.075-1.299-.165-1.903.586-.274 1.084-.705 1.439-1.246.354-.54.551-1.17.569-1.816zM9.662 14.85l-3.429-3.428 1.293-1.302 2.072 2.072 4.4-4.794 1.347 1.246z" />
              </svg>
            </div>
            <p className="text-gray-500 text-sm">@{data?.user?.handle || handle}</p>
          </div>
        </div>

        {/* Time remaining badge */}
        <div className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-[5px] text-xs font-inter-tight ${
          isEnded
            ? "bg-gray-200 dark:bg-gray-800 text-gray-500 dark:text-gray-400"
            : "bg-gray-100 dark:bg-white/10 text-gray-700 dark:text-white"
        }`}>
          <Clock className="w-3.5 h-3.5" />
          <span className="font-medium">{isEnded ? "Market Ended" : `${timeRemaining} remaining`}</span>
        </div>
      </div>

      {/* Main Counter */}
      <div className="p-4 pt-6">
        <div className="text-center mb-5">
          <div className="text-xs text-gray-500 uppercase tracking-wider mb-1">Post Count</div>
          <div className="text-5xl font-monument font-black text-gray-900 dark:text-white mb-1">
            {totalPosts.toLocaleString()}
          </div>
          <div className="text-gray-500 text-xs font-inter-tight">
            {formatDate(startDate)} - {formatDate(endDate)}
          </div>
        </div>

        {/* Target Progress Bar (if set) */}
        {targetCount && (
          <div className="mb-5">
            <div className="flex items-center justify-between text-xs mb-2">
              <span className="text-gray-500 dark:text-gray-400 flex items-center gap-1">
                <Target className="w-3 h-3" />
                Target: {targetCount.toLocaleString()}
              </span>
              <span className={`font-medium ${totalPosts >= targetCount ? "text-[#00FF9D]" : "text-gray-900 dark:text-white"}`}>
                {totalPosts >= targetCount ? "✓ Reached!" : `${(targetCount - totalPosts).toLocaleString()} to go`}
              </span>
            </div>
            <div className="h-3 bg-gray-200 dark:bg-white/5 rounded-[5px] overflow-hidden">
              <div
                className={`h-full transition-colors duration-700 ease-out rounded-[5px] ${
                  totalPosts >= targetCount
                    ? "bg-gradient-to-r from-[#00FF9D] to-[#00CC7D]"
                    : "bg-gray-900 dark:bg-white"
                }`}
                style={{ width: `${progressPercent}%` }}
              />
            </div>
            <div className="flex justify-between text-[10px] text-gray-500 dark:text-gray-600 mt-1">
              <span>0</span>
              <span>{Math.round(progressPercent)}%</span>
              <span>{targetCount.toLocaleString()}</span>
            </div>
          </div>
        )}

        {/* Daily Activity Chart */}
        {chartData.length > 0 && (
          <div className="mb-5">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-1.5 text-gray-500 dark:text-gray-400 text-xs">
                <TrendingUp className="w-3.5 h-3.5" />
                <span>Daily Posts</span>
              </div>
              <span className="text-[10px] text-gray-500 dark:text-gray-600">{chartData.length} days</span>
            </div>
            <div className="flex items-end gap-1.5 px-1" style={{ height: "80px" }}>
              {chartData.map((day, i) => {
                const barHeight = Math.max((day.count / maxCount) * 70, 6); // 70px max height
                const isToday = i === chartData.length - 1;
                return (
                  <div key={i} className="flex-1 flex flex-col items-center justify-end gap-1.5 group h-full">
                    {/* Bar with tooltip */}
                    <div className="relative w-full flex items-end justify-center flex-1">
                      <div
                        className={`w-full rounded-t-sm transition-colors duration-300 ${
                          isToday
                            ? "bg-gray-900 dark:bg-white"
                            : "bg-gray-300 group-hover:bg-gray-400 dark:bg-white/30 dark:group-hover:bg-white/50"
                        }`}
                        style={{ height: `${barHeight}px` }}
                      />
                      {/* Tooltip on hover */}
                      <div className="absolute -top-6 left-1/2 -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity bg-gray-900 dark:bg-black px-2 py-1 rounded text-[10px] text-white whitespace-nowrap z-10 pointer-events-none">
                        {day.count} posts
                      </div>
                    </div>
                    {/* Date label */}
                    <span className={`text-[9px] ${isToday ? "text-gray-900 dark:text-white" : "text-gray-500 dark:text-gray-600"}`}>
                      {new Date(day.date).getDate()}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Daily breakdown list */}
        <div className="space-y-1.5 max-h-40 overflow-y-auto scrollbar-hide">
          {[...dailyMetrics].reverse().map((day, i) => (
            <div
              key={i}
              className="flex items-center justify-between px-3 py-2 bg-gray-100 hover:bg-gray-200 dark:bg-white/5 dark:hover:bg-white/10 rounded-[5px] text-xs transition-colors"
            >
              <span className="text-gray-500 dark:text-gray-400 font-inter-tight">
                {formatDate(day.date)}
              </span>
              <div className="flex items-center gap-3">
                <span className="text-gray-900 dark:text-white font-semibold">
                  +{day.count}
                </span>
                <span className="text-gray-500 dark:text-gray-600 text-[10px] tabular-nums">
                  {day.cumulative.toLocaleString()} total
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Full Dashboard Button (only for Elon Musk) */}
      {handle.toLowerCase() === "elonmusk" && (
        <div className="px-4 pb-3">
          <Link
            href="/elon"
            className="flex items-center justify-center gap-2 w-full py-2.5 bg-[#7C3AED] hover:bg-[#6D28D9] dark:bg-[#CCFF00] dark:hover:bg-[#d4ff33] text-white dark:text-black font-bold text-sm rounded-[5px] transition-colors"
          >
            <Zap className="w-4 h-4" />
            View Full Dashboard
            <ExternalLink className="w-3.5 h-3.5" />
          </Link>
        </div>
      )}

      {/* Footer */}
      <div className="px-4 py-2.5 border-t border-gray-200 dark:border-[#1A1A1A] bg-gray-50 dark:bg-white/[0.02]">
        <a
          href={`https://xtracker.polymarket.com/user/${data?.user?.handle || handle}`}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center justify-center gap-1.5 text-[10px] text-gray-500 dark:text-gray-600 hover:text-gray-700 dark:hover:text-gray-400 transition-colors"
        >
          <XIcon className="w-3 h-3" />
          <span>Live tracking by XTracker</span>
          <ExternalLink className="w-2.5 h-2.5" />
        </a>
      </div>
    </div>
  );
}
